import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Building2, Home, Calculator, Sparkles } from "lucide-react";
import axiosInstance from '@/config/axios';
import { toast } from "sonner";

const CHILEAN_COMMUNES = [
  "Las Condes", "Vitacura", "Lo Barnechea", "Providencia",
  "Santiago", "Ñuñoa", "La Reina", "La Florida",
  "Puente Alto", "San Bernardo", "Huechuraba", "Quilicura",
  "Colina", "Viña del Mar", "Valparaíso", "Concón"
].sort();

interface ValuationCalculatorProps {
  onSuccess: (valuation: any) => void;
}

export function ValuationCalculator({ onSuccess }: ValuationCalculatorProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    address: '',
    city: 'Santiago',
    propertyType: 'departamento',
    area: '',
    totalArea: '',
    bedrooms: '2',
    bathrooms: '1',
    parking: '1',
    yearBuilt: new Date().getFullYear().toString(),
    condition: 'bueno'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const payload = {
        propertyData: {
          ...formData,
          area: Number(formData.area),
          totalArea: Number(formData.totalArea || formData.area),
          bedrooms: Number(formData.bedrooms),
          bathrooms: Number(formData.bathrooms),
          parking: Number(formData.parking),
          yearBuilt: Number(formData.yearBuilt),
          country: 'Chile'
        }
      };

      const response = await axiosInstance.post('/api/valuations/calculate', payload);
      toast.success("Tasación generada con éxito");
      onSuccess(response.data);
    } catch (error) {
      console.error("Error calculating valuation:", error);
      toast.error("Error al calcular la tasación");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="brutalist-border p-8 bg-parchment relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-terracotta/5 rounded-full blur-3xl" />
      
      <div className="flex items-center gap-4 mb-8 border-b-2 border-ink pb-6">
        <div className="bg-ink p-3">
          <Calculator className="h-6 w-6 text-parchment" />
        </div>
        <div>
          <h2 className="text-2xl font-display font-black uppercase italic">Nueva Tasación IA</h2>
          <p className="text-xs font-serif italic text-ink/60">Motor HousePricing v2.0 — Chilean Market</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Informacion Básica */}
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest">Identificador / Nombre</Label>
              <Input 
                placeholder="Ej: Depto Inversión Vitacura" 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest">Comuna</Label>
              <Select 
                value={formData.city} 
                onValueChange={(v) => setFormData({...formData, city: v})}
              >
                <SelectTrigger className="brutalist-border-sm bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-2 border-ink rounded-none">
                  {CHILEAN_COMMUNES.map(commune => (
                    <SelectItem key={commune} value={commune} className="uppercase text-xs font-black">{commune}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest">Dirección (Referencial)</Label>
              <Input 
                placeholder="Ej: Av. Vitacura 1234" 
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest">Tipo</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={formData.propertyType === 'departamento' ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={() => setFormData({...formData, propertyType: 'departamento'})}
                  >
                    <Building2 className="mr-2 h-4 w-4" /> Depto
                  </Button>
                  <Button
                    type="button"
                    variant={formData.propertyType === 'casa' ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={() => setFormData({...formData, propertyType: 'casa'})}
                  >
                    <Home className="mr-2 h-4 w-4" /> Casa
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest">Estado</Label>
                <Select value={formData.condition} onValueChange={(v) => setFormData({...formData, condition: v})}>
                  <SelectTrigger className="brutalist-border-sm bg-white h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-2 border-ink rounded-none">
                    <SelectItem value="excelente">Excelente</SelectItem>
                    <SelectItem value="bueno">Bueno</SelectItem>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="necesita_reparaciones">A Remodelar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Atributos Técnicos */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest">M² Útiles</Label>
                <Input 
                  type="number" 
                  placeholder="85" 
                  value={formData.area}
                  onChange={(e) => setFormData({...formData, area: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest">M² Totales</Label>
                <Input 
                  type="number" 
                  placeholder="90" 
                  value={formData.totalArea}
                  onChange={(e) => setFormData({...formData, totalArea: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest">Dorms</Label>
                <Input 
                  type="number" 
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({...formData, bedrooms: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest">Baños</Label>
                <Input 
                  type="number" 
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase tracking-widest">Estac.</Label>
                <Input 
                  type="number" 
                  value={formData.parking}
                  onChange={(e) => setFormData({...formData, parking: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase tracking-widest">Año de Construcción</Label>
              <Input 
                type="number" 
                value={formData.yearBuilt}
                onChange={(e) => setFormData({...formData, yearBuilt: e.target.value})}
              />
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full h-16 bg-ink text-parchment hover:bg-terracotta transition-all group"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Sparkles className="animate-spin h-5 w-5" /> PROCESANDO...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    GENERAR INFORME PROFESIONAL <Sparkles className="h-5 w-5 group-hover:scale-125 transition-transform" />
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Card>
  );
}
